import React, { Fragment, useState, useEffect } from 'react';
import {
  Truck,
  Plus,
  Edit2,
  Trash2,
  Star,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  BarChart3,
  FileText,
  Search,
} from 'lucide-react';
import { suppliersApi, usersApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';

const STATUS_LABELS = {
  pending: { label: 'Pendente', color: 'bg-gray-500/20 text-gray-400' },
  approved: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  conditional: { label: 'Condicional', color: 'bg-yellow-500/20 text-yellow-400' },
  blocked: { label: 'Bloqueado', color: 'bg-red-500/20 text-red-400' },
  inactive: { label: 'Inativo', color: 'bg-gray-500/20 text-gray-400' },
};

const CATEGORY_LABELS = {
  raw_material: 'Matéria-prima',
  packaging: 'Embalagem',
  services: 'Serviços',
  equipment: 'Equipamentos',
  calibration: 'Calibração',
  maintenance: 'Manutenção',
  logistics: 'Logística',
  other: 'Outros',
};

const Suppliers = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_classification: {}, critical: 0, pending_requalification: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [filters, setFilters] = useState({ category: '', status: '', classification: '' });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suppliersRes, statsRes] = await Promise.all([
        suppliersApi.list(filters),
        suppliersApi.stats(),
      ]);
      setSuppliers(suppliersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadEvaluations = async (supplierId) => {
    try {
      const res = await suppliersApi.listEvaluations(supplierId);
      setEvaluations(res.data);
    } catch (error) {
      toast.error('Erro ao carregar avaliações');
    }
  };

  const handleSelectSupplier = async (supplier) => {
    setSelectedSupplier(supplier);
    await loadEvaluations(supplier.id);
    setActiveTab('evaluations');
  };

  const handleCreateSupplier = async (data) => {
    try {
      await suppliersApi.create(data);
      toast.success('Fornecedor criado com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar fornecedor'));
    }
  };

  const handleUpdateSupplier = async (data) => {
    try {
      await suppliersApi.update(editingSupplier.id, data);
      toast.success('Fornecedor atualizado com sucesso');
      setShowModal(false);
      setEditingSupplier(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este fornecedor?')) return;
    try {
      await suppliersApi.delete(id);
      toast.success('Fornecedor excluído');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir fornecedor');
    }
  };

  const handleCreateEvaluation = async (data) => {
    try {
      await suppliersApi.createEvaluation(selectedSupplier.id, data);
      toast.success('Avaliação registrada com sucesso');
      setShowEvalModal(false);
      loadData();
      await loadEvaluations(selectedSupplier.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao registrar avaliação'));
    }
  };

  const tabs = [
    { id: 'list', label: 'Fornecedores', icon: Truck },
    { id: 'evaluations', label: 'Avaliações', icon: Star, disabled: !selectedSupplier },
    { id: 'documents', label: 'Documentos', icon: FileText, disabled: !selectedSupplier },
  ];

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Truck className="text-green-500" />
            Gestão de Fornecedores
          </h1>
          <p className="text-gray-400 mt-1">Cadastro e avaliação de fornecedores</p>
        </div>
        <button
          onClick={() => { setEditingSupplier(null); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-supplier-btn"
        >
          <Plus size={20} />
          Novo Fornecedor
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Aprovados</p>
          <p className="text-2xl font-bold text-green-400">{stats.by_status?.approved || 0}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Condicionais</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.by_status?.conditional || 0}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Bloqueados</p>
          <p className="text-2xl font-bold text-red-400">{stats.by_status?.blocked || 0}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Requalificação Pendente</p>
          <p className="text-2xl font-bold text-orange-400">{stats.pending_requalification}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-green-500 text-green-400'
                : tab.disabled
                ? 'border-transparent text-gray-600 cursor-not-allowed'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : activeTab === 'list' ? (
          <SuppliersTable
            suppliers={suppliers}
            onSelect={handleSelectSupplier}
            onEdit={(sup) => { setEditingSupplier(sup); setShowModal(true); }}
            onDelete={handleDeleteSupplier}
            filters={filters}
            setFilters={setFilters}
          />
        ) : activeTab === 'evaluations' ? (
          <EvaluationsView
            supplier={selectedSupplier}
            evaluations={evaluations}
            onEvaluate={() => setShowEvalModal(true)}
          />
        ) : (
          <DocumentsView supplier={selectedSupplier} />
        )}
      </div>

      {/* Supplier Modal */}
      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          users={users}
          onClose={() => { setShowModal(false); setEditingSupplier(null); }}
          onSave={editingSupplier ? handleUpdateSupplier : handleCreateSupplier}
        />
      )}

      {/* Evaluation Modal */}
      {showEvalModal && (
        <EvaluationModal
          supplier={selectedSupplier}
          onClose={() => setShowEvalModal(false)}
          onSave={handleCreateEvaluation}
        />
      )}
    </div>
  );
};

const SuppliersTable = ({ suppliers, onSelect, onEdit, onDelete, filters, setFilters }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSuppliers = suppliers.filter(sup =>
    !searchTerm ||
    sup.legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sup.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sup.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (sup) => <td className="font-mono text-green-400">{sup.code}</td> },
    { key: 'name', label: 'Razão Social', render: (sup) => (
      <td className="font-medium text-white">
        {sup.legal_name}
        {sup.is_critical && <span className="ml-2 text-xs text-red-400">CRÍTICO</span>}
      </td>
    ) },
    { key: 'category', label: 'Categoria', render: (sup) => <td>{CATEGORY_LABELS[sup.category] || sup.category}</td> },
    { key: 'city', label: 'Cidade', render: (sup) => <td>{sup.city || '-'}</td> },
    { key: 'status', label: 'Status', render: (sup) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[sup.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {STATUS_LABELS[sup.status]?.label || sup.status}
        </span>
      </td>
    ) },
    { key: 'score', label: 'Nota', render: (sup) => <td>{sup.current_score?.toFixed(1) || '-'}</td> },
    { key: 'classification', label: 'Classe', render: (sup) => (
      <td>
        {sup.classification && (
          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
            sup.classification === 'A' ? 'bg-green-500/20 text-green-400' :
            sup.classification === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {sup.classification}
          </span>
        )}
      </td>
    ) },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (sup) => (
      <td>
        <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onEdit(sup)} className="p-1 text-gray-400 hover:text-blue-400">
            <Edit2 size={18} />
          </button>
          <button onClick={() => onDelete(sup.id)} className="p-1 text-gray-400 hover:text-red-400">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'suppliers', columns: COLUMNS });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar fornecedores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <select
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todas Categorias</option>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todos Status</option>
          {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.classification}
          onChange={(e) => setFilters({ ...filters, classification: e.target.value })}
          className="input px-3 w-full sm:w-60"
        >
          <option value="">Todas Classes</option>
          <option value="A">Classe A</option>
          <option value="B">Classe B</option>
          <option value="C">Classe C</option>
        </select>
        <div className="flex items-center">
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {visibleColumns.map(col => (
                <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-8 text-gray-500">Nenhum fornecedor encontrado</td>
              </tr>
            ) : (
              filteredSuppliers.map((sup) => (
                <tr key={sup.id} onClick={() => onSelect(sup)}>
                  {visibleColumns.map(col => (
                    <Fragment key={col.key}>{col.render(sup)}</Fragment>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DOC_TYPE_LABELS = {
  licenca: 'Licença', certificado: 'Certificado', contrato: 'Contrato',
  alvara: 'Alvará', iso: 'Certificado ISO', outro: 'Outro',
};
const DOC_STATUS_COLORS = {
  vigente: 'text-green-400', a_vencer: 'text-yellow-400', vencido: 'text-red-400',
};

const DocumentsView = ({ supplier }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    document_name: '', document_type: 'certificado', document_number: '',
    issuer: '', issue_date: '', expiration_date: '', notes: '',
  });

  const loadDocs = async () => {
    try {
      setLoading(true);
      const res = await suppliersApi.listDocuments(supplier.id);
      setDocs(res.data);
    } catch { toast.error('Erro ao carregar documentos'); } finally { setLoading(false); }
  };

  useEffect(() => { loadDocs(); }, [supplier.id]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: form.issue_date, label: 'Emissão' },
      { value: form.expiration_date, label: 'Vencimento' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      await suppliersApi.createDocument(supplier.id, form);
      toast.success('Documento adicionado');
      setShowForm(false);
      setForm({ document_name: '', document_type: 'certificado', document_number: '', issuer: '', issue_date: '', expiration_date: '', notes: '' });
      loadDocs();
    } catch { toast.error('Erro ao adicionar documento'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir documento?')) return;
    try { await suppliersApi.deleteDocument(id); toast.success('Removido'); loadDocs(); }
    catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-white">{supplier.legal_name}</h3>
          <p className="text-sm text-gray-400">Documentos e Certificados</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
          <Plus size={16} />Adicionar Documento
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-lg p-4">
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-400 mb-1">Nome do Documento *</label>
              <input className="input w-full" required value={form.document_name} onChange={e => setForm({ ...form, document_name: e.target.value })} placeholder="Ex: ISO 9001:2015, AVCB..." />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Tipo</label>
              <select className="input w-full" value={form.document_type} onChange={e => setForm({ ...form, document_type: e.target.value })}>
                {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Número</label>
              <input className="input w-full" value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Órgão Emissor</label>
              <input className="input w-full" value={form.issuer} onChange={e => setForm({ ...form, issuer: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Emissão</label>
              <input type="date" className="input w-full" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} min="1900-01-01" max="2099-12-31" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Vencimento</label>
              <input type="date" className="input w-full" value={form.expiration_date} onChange={e => setForm({ ...form, expiration_date: e.target.value })} min="1900-01-01" max="2099-12-31" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-400 mb-1">Observações</label>
              <input className="input w-full" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-end gap-2">
              <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-green-500" size={24} /></div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead><tr><th>Documento</th><th>Tipo</th><th>Número</th><th>Emissor</th><th>Emissão</th><th>Vencimento</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {docs.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-gray-500">Nenhum documento cadastrado</td></tr>
              ) : docs.map(d => (
                <tr key={d.id}>
                  <td className="font-medium text-white">{d.document_name}</td>
                  <td className="text-gray-400">{DOC_TYPE_LABELS[d.document_type] || d.document_type || '-'}</td>
                  <td className="text-gray-400">{d.document_number || '-'}</td>
                  <td className="text-gray-400">{d.issuer || '-'}</td>
                  <td className="text-gray-400">{d.issue_date || '-'}</td>
                  <td className="text-gray-400">{d.expiration_date || '-'}</td>
                  <td>
                    {d.status && <span className={`font-semibold capitalize ${DOC_STATUS_COLORS[d.status] || 'text-gray-400'}`}>
                      {d.status === 'vigente' ? 'Vigente' : d.status === 'a_vencer' ? 'A Vencer' : d.status === 'vencido' ? 'Vencido' : d.status}
                    </span>}
                  </td>
                  <td>
                    <button onClick={() => handleDelete(d.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EvaluationsView = ({ supplier, evaluations, onEvaluate }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-semibold text-white">{supplier.legal_name}</h3>
        <p className="text-sm text-gray-400">{supplier.code} • {CATEGORY_LABELS[supplier.category]}</p>
      </div>
      <button onClick={onEvaluate} className="btn-primary" data-testid="evaluate-supplier-btn">
        <Star size={20} />
        Nova Avaliação
      </button>
    </div>

    {/* Current Score */}
    {supplier.current_score && (
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Nota Atual</p>
            <p className="text-3xl font-bold text-white">{supplier.current_score.toFixed(1)}</p>
          </div>
          <div className={`text-5xl font-bold ${
            supplier.classification === 'A' ? 'text-green-400' :
            supplier.classification === 'B' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {supplier.classification}
          </div>
        </div>
      </div>
    )}

    {/* Evaluations Table */}
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Período</th>
            <th>Qualidade</th>
            <th>Entrega</th>
            <th>Preço</th>
            <th>Serviço</th>
            <th>Nota Geral</th>
            <th>Classe</th>
          </tr>
        </thead>
        <tbody>
          {evaluations.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center py-8 text-gray-500">Nenhuma avaliação registrada</td>
            </tr>
          ) : (
            evaluations.map((ev) => (
              <tr key={ev.id}>
                <td>{formatDateBR(ev.evaluation_date)}</td>
                <td>{ev.period || '-'}</td>
                <td>{ev.quality_score?.toFixed(1) || '-'}</td>
                <td>{ev.delivery_score?.toFixed(1) || '-'}</td>
                <td>{ev.price_score?.toFixed(1) || '-'}</td>
                <td>{ev.service_score?.toFixed(1) || '-'}</td>
                <td className="font-medium text-white">{ev.overall_score?.toFixed(1) || '-'}</td>
                <td>
                  {ev.classification && (
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      ev.classification === 'A' ? 'bg-green-500/20 text-green-400' :
                      ev.classification === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {ev.classification}
                    </span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const SupplierModal = ({ supplier, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    legal_name: supplier?.legal_name || '',
    trade_name: supplier?.trade_name || '',
    tax_id: supplier?.tax_id || '',
    category: supplier?.category || 'other',
    products_services: supplier?.products_services || '',
    address: supplier?.address || '',
    city: supplier?.city || '',
    state: supplier?.state || '',
    country: supplier?.country || 'Brasil',
    postal_code: supplier?.postal_code || '',
    phone: supplier?.phone || '',
    email: supplier?.email || '',
    website: supplier?.website || '',
    contact_name: supplier?.contact_name || '',
    contact_phone: supplier?.contact_phone || '',
    contact_email: supplier?.contact_email || '',
    certifications: supplier?.certifications || '',
    observations: supplier?.observations || '',
    responsible_id: supplier?.responsible_id || '',
    is_critical: supplier?.is_critical || false,
    status: supplier?.status || 'pending',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { ...formData };
    if (!data.responsible_id) delete data.responsible_id;
    try {
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {supplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Razão Social *</label>
              <input
                type="text"
                value={formData.legal_name}
                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome Fantasia</label>
              <input
                type="text"
                value={formData.trade_name}
                onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">CNPJ</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Categoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="input w-full"
              >
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Telefone</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Cidade</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Estado</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Contato Principal</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email do Contato</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="input w-full"
              />
            </div>
            {supplier && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input w-full"
                >
                  {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
              <select
                value={formData.responsible_id}
                onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Produtos/Serviços</label>
              <textarea
                value={formData.products_services}
                onChange={(e) => setFormData({ ...formData, products_services: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Certificações</label>
              <input
                type="text"
                value={formData.certifications}
                onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                className="input w-full"
                placeholder="Ex: ISO 9001, ISO 14001"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={formData.is_critical}
                  onChange={(e) => setFormData({ ...formData, is_critical: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Fornecedor Crítico
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const EvaluationModal = ({ supplier, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    period: `${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    evaluation_type: 'periodic',
    quality_score: '',
    delivery_score: '',
    price_score: '',
    service_score: '',
    documentation_score: '',
    strengths: '',
    weaknesses: '',
    action_required: '',
    status_decision: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = {
      ...formData,
      quality_score: formData.quality_score ? parseFloat(formData.quality_score) : null,
      delivery_score: formData.delivery_score ? parseFloat(formData.delivery_score) : null,
      price_score: formData.price_score ? parseFloat(formData.price_score) : null,
      service_score: formData.service_score ? parseFloat(formData.service_score) : null,
      documentation_score: formData.documentation_score ? parseFloat(formData.documentation_score) : null,
    };
    if (!data.status_decision) delete data.status_decision;
    try {
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Nova Avaliação</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Período</label>
              <input
                type="text"
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                className="input w-full"
                placeholder="Ex: 2026-Q1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
              <select
                value={formData.evaluation_type}
                onChange={(e) => setFormData({ ...formData, evaluation_type: e.target.value })}
                className="input w-full"
              >
                <option value="initial">Inicial</option>
                <option value="periodic">Periódica</option>
                <option value="special">Especial</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-white font-medium mb-3">Notas (0-100)</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Qualidade</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.quality_score}
                  onChange={(e) => setFormData({ ...formData, quality_score: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Entrega</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.delivery_score}
                  onChange={(e) => setFormData({ ...formData, delivery_score: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Preço</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.price_score}
                  onChange={(e) => setFormData({ ...formData, price_score: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Atendimento</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.service_score}
                  onChange={(e) => setFormData({ ...formData, service_score: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Documentação</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.documentation_score}
                  onChange={(e) => setFormData({ ...formData, documentation_score: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Pontos Fortes</label>
            <textarea
              value={formData.strengths}
              onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Pontos Fracos</label>
            <textarea
              value={formData.weaknesses}
              onChange={(e) => setFormData({ ...formData, weaknesses: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Ações Necessárias</label>
            <textarea
              value={formData.action_required}
              onChange={(e) => setFormData({ ...formData, action_required: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar Avaliação'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Suppliers;
